import { sanitizeHtml } from '@/routes/mail/composeHtml';
import type { StagedAttachment } from '@/api';
import type { ComposeSeed } from '@/routes/mail/ComposeDialog';

/**
 * Popping the composer out into its own window (ADR 0020, applied to compose).
 *
 * Same bargain as the message window: a real `window.open` onto a real route,
 * so it can be dragged to the other monitor, closed by the OS, and — being
 * same-origin — carries the `__Host-` cookie trio and signs every request with
 * `X-Session-Proof` through the one shared transport. There is no second
 * request path here, and nothing about the session is handed across.
 *
 * `noopener` is deliberately NOT passed: the window is our own document, and
 * without the handle a second Pop out could not focus the window that is
 * already open, nor could the window hand the draft back.
 *
 * ## Why the draft travels through storage and not the URL
 *
 * The half-written message must survive the pop-out, and a body in a query
 * string would be capped by the browser's URL length, land in history, and show
 * up in any log that records paths. Instead the opener writes the draft under a
 * one-shot key and passes only that key; the window reads it once and deletes
 * it, so the same draft can never be resurrected by a later window.
 */

const HANDOFF_PREFIX = 'ols.compose.handoff.';

/** The route a compose window renders. `?draft=` is the handoff key, if any. */
export function composeWindowPath(accountId: string, handoffId?: string): string {
  const path = `/compose/${encodeURIComponent(accountId)}`;
  return handoffId === undefined ? path : `${path}?draft=${encodeURIComponent(handoffId)}`;
}

/**
 * One window per handoff, not one per account: two drafts popped out of the
 * same mailbox are two messages, and reusing a name would replace the first.
 */
export function composeWindowName(handoffId: string): string {
  return `ols-compose-${handoffId}`;
}

function handoffKey(handoffId: string): string {
  return `${HANDOFF_PREFIX}${handoffId}`;
}

/** Stores the draft for the window about to be opened. Never throws. */
export function stashComposeDraft(handoffId: string, draft: ComposeSeed): void {
  try {
    localStorage.setItem(handoffKey(handoffId), JSON.stringify(draft));
  } catch {
    /* private mode, blocked site data — the window opens with empty fields */
  }
}

/**
 * Reads the handed-over draft and drops it in the same breath.
 *
 * Deleting on read is what keeps the key one-shot: a reload of the window
 * therefore starts empty rather than re-opening a message that may already have
 * been sent. Unknown fields are ignored — this is data coming out of storage,
 * so it is validated by shape rather than trusted.
 */
export function takeComposeDraft(handoffId: string): ComposeSeed | undefined {
  let raw: string | null;
  try {
    raw = localStorage.getItem(handoffKey(handoffId));
    localStorage.removeItem(handoffKey(handoffId));
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  return seedFromStorage(parsed);
}

/** The largest body a stored seed may carry, in UTF-16 code units (~2 MiB). */
export const MAX_STORED_BODY = 2 * 1024 * 1024;
/** The most attachments a stored seed may name: the server's own cap. */
export const MAX_STORED_ATTACHMENTS = 20;
/** Bounds on the short fields, generous for any real header. */
const MAX_STORED_FIELD = 64 * 1024;

/**
 * A composer seed read back out of storage: the pop-out hand-over, or the
 * local draft store (`routes/mail/draftStore.ts`).
 *
 * Both are reachable by anything running on the origin, so the value is
 * validated by shape rather than trusted: only a plain object is read, only
 * its own fields of the right type and size are kept, and the HTML is
 * sanitized on the way out (`sanitizeHtml` keeps the reply quote's
 * `gmail_quote` classes, so the composer folds it again). Anything that is not
 * a plain object, or whose body or attachment list is over the bounds, is
 * refused whole rather than truncated: a half-restored message is worse than
 * an honest "could not be read".
 */
export function seedFromStorage(parsed: unknown): ComposeSeed | undefined {
  if (!isPlainObject(parsed)) return undefined;
  const draft = parsed;
  const text = (key: string, max = MAX_STORED_FIELD): string | undefined => {
    if (!Object.prototype.hasOwnProperty.call(draft, key)) return undefined;
    const value = draft[key];
    return typeof value === 'string' && value.length <= max ? value : undefined;
  };
  for (const key of ['body', 'bodyHtml']) {
    const value = Object.prototype.hasOwnProperty.call(draft, key) ? draft[key] : undefined;
    if (typeof value === 'string' && value.length > MAX_STORED_BODY) return undefined;
  }
  const attachmentsValue = Object.prototype.hasOwnProperty.call(draft, 'attachments') ? draft['attachments'] : undefined;
  if (Array.isArray(attachmentsValue) && attachmentsValue.length > MAX_STORED_ATTACHMENTS) return undefined;
  const mode = text('mode');
  const draftUuid = text('draftUuid');
  const localDraftId = text('localDraftId');
  return {
    ...(text('to') !== undefined ? { to: text('to')! } : {}),
    ...(text('cc') !== undefined ? { cc: text('cc')! } : {}),
    ...(text('bcc') !== undefined ? { bcc: text('bcc')! } : {}),
    ...(text('subject') !== undefined ? { subject: text('subject')! } : {}),
    ...(text('body', MAX_STORED_BODY) !== undefined ? { body: text('body', MAX_STORED_BODY)! } : {}),
    // Sanitized on the way out of storage, not just on the way into the
    // editor. This is where markup from outside this component's control
    // re-enters it: storage is reachable by anything running on the origin,
    // and the value is handed to a composer that will send it.
    ...(text('bodyHtml', MAX_STORED_BODY) !== undefined
      ? { bodyHtml: sanitizeHtml(text('bodyHtml', MAX_STORED_BODY)!) }
      : {}),
    ...(text('inReplyToMessageId') !== undefined
      ? { inReplyToMessageId: text('inReplyToMessageId')! }
      : {}),
    ...(text('forwardedMessageId') !== undefined
      ? { forwardedMessageId: text('forwardedMessageId')! }
      : {}),
    // This function rebuilds the seed field by field, which is right for
    // untrusted storage and has a cost: every field added to ComposeSeed must be
    // added HERE too, or it is silently dropped in transit. That has now
    // happened three times — bodyHtml, then draftId (which let the window start
    // a second draft beside the first), then attachments (which let the
    // window's first autosave store the draft without its files), then
    // forwardedMessageId and the 'forward' mode, then nextcloudUploadIds
    // (which would have let the window send the Nextcloud links without
    // marking them sent, to be swept as orphans a month later).
    ...(text('draftId') !== undefined ? { draftId: text('draftId')! } : {}),
    // ...and draftUuid with draftUpdatedAt (ADR 0100), without which the
    // window would save as a second draft and be refused as changed, and
    // localDraftId, without which it would keep a second local copy.
    ...(draftUuid !== undefined && /^[0-9a-f-]{36}$/.test(draftUuid) ? { draftUuid } : {}),
    ...(text('draftUpdatedAt') !== undefined ? { draftUpdatedAt: text('draftUpdatedAt')! } : {}),
    ...(localDraftId !== undefined && /^[0-9a-f-]{36}$/.test(localDraftId) ? { localDraftId } : {}),
    ...(stagedAttachments(attachmentsValue) ?? {}),
    ...(uploadIds(Object.prototype.hasOwnProperty.call(draft, 'nextcloudUploadIds') ? draft['nextcloudUploadIds'] : undefined) ?? {}),
    ...(draft['edited'] === true ? { edited: true } : {}),
    // The chosen From (ADR 0099). Only a request: the composer keeps it only
    // if it is one of the account's own addresses, and the server checks again.
    ...(text('from') !== undefined ? { from: text('from')! } : {}),
    mode: mode === 'reply' || mode === 'reply-all' || mode === 'forward' ? mode : 'new',
  };
}

/**
 * A plain object, as JSON.parse and IndexedDB's structured clone produce:
 * nothing with a prototype of its own, and never an array.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * The Nextcloud uploads a hand-over names (ADR 0084 D13), validated by shape.
 * The server owner-checks every id, as it does the staged attachments'.
 */
function uploadIds(value: unknown): { nextcloudUploadIds: string[] } | undefined {
  if (!Array.isArray(value)) return undefined;
  const kept = value
    .slice(0, 100)
    .filter((id): id is string => typeof id === 'string' && id !== '' && id.length <= 256);
  return kept.length > 0 ? { nextcloudUploadIds: kept } : undefined;
}

/**
 * The staged uploads a hand-over names, validated as untrusted input.
 *
 * Anything on the origin can write localStorage, so an entry is only kept if
 * it has the shape the server produced. The id is not trusted either way: the
 * send and the draft save both resolve it against this owner's staging store,
 * which refuses an id that is not theirs.
 */
function stagedAttachments(value: unknown): { attachments: StagedAttachment[] } | undefined {
  if (!Array.isArray(value)) return undefined;
  const kept: StagedAttachment[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) continue;
    if (!isPlainObject(item)) continue;
    const a = item;
    if (typeof a.id !== 'string' || a.id === '' || a.id.length > 256) continue;
    if (typeof a.filename !== 'string' || a.filename.length > 1024) continue;
    if (typeof a.sizeBytes !== 'number' || !Number.isFinite(a.sizeBytes) || a.sizeBytes < 0) continue;
    kept.push({
      id: a.id,
      filename: a.filename,
      contentType: typeof a.contentType === 'string' ? a.contentType : 'application/octet-stream',
      sizeBytes: a.sizeBytes,
      inline: a.inline === true,
      ...(typeof a.contentId === 'string' ? { contentId: a.contentId } : {}),
    });
  }
  return kept.length > 0 ? { attachments: kept } : undefined;
}

export const COMPOSE_WINDOW_DEFAULT_WIDTH = 720;
export const COMPOSE_WINDOW_DEFAULT_HEIGHT = 640;

export interface OpenComposeWindowOptions {
  accountId: string;
  /** The fields as they stand right now; an empty composer hands over an empty seed. */
  draft: ComposeSeed;
}

export interface OpenComposeWindowResult {
  /** `false` when the browser or an extension blocked the window. */
  opened: boolean;
  url: string;
}

/**
 * Opens the compose window, carrying the current draft into it.
 *
 * Returns `{opened: false}` when `window.open` answered `null`, so the caller
 * can keep the in-page composer — and the draft in it — rather than dropping a
 * half-written message into a window that never appeared.
 */
export function openComposeWindow({
  accountId,
  draft,
}: OpenComposeWindowOptions): OpenComposeWindowResult {
  const handoffId = newHandoffId();
  stashComposeDraft(handoffId, draft);
  const url = composeWindowPath(accountId, handoffId);

  const width = COMPOSE_WINDOW_DEFAULT_WIDTH;
  const availHeight = window.screen?.availHeight;
  const heightCap = typeof availHeight === 'number' && availHeight > 0 ? availHeight - 80 : undefined;
  const height =
    heightCap === undefined
      ? COMPOSE_WINDOW_DEFAULT_HEIGHT
      : Math.min(COMPOSE_WINDOW_DEFAULT_HEIGHT, heightCap);

  // Centred over the opener, not over the screen: the window the user is
  // looking at is the one it should land on.
  const left = Math.round((window.screenX ?? 0) + ((window.outerWidth || width) - width) / 2);
  const top = Math.round((window.screenY ?? 0) + ((window.outerHeight || height) - height) / 2);

  const features = ['popup=yes', `width=${width}`, `height=${height}`, `left=${left}`, `top=${top}`].join(',');

  const handle = window.open(url, composeWindowName(handoffId), features);
  if (!handle) {
    // Nothing opened, so the stashed copy would sit in storage for ever.
    try {
      localStorage.removeItem(handoffKey(handoffId));
    } catch {
      /* nothing was stored in the first place */
    }
    return { opened: false, url };
  }
  try {
    handle.focus();
  } catch {
    /* a window we are allowed to open but not to focus is still open */
  }
  return { opened: true, url };
}

function newHandoffId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
